(() => {
  'use strict';

  const config = window.CampaignCatalog;
  if (!config || !config.enabled) return;

  const markerKey = config.markerKey || 'campaign_catalog';

  const hostFromValue = (value) => {
    try {
      return new URL(String(value || '').includes('://') ? value : `https://${value}`).hostname.toLowerCase();
    } catch (error) {
      return '';
    }
  };

  const shopHosts = new Set(
    [window.location.hostname, ...(config.shopDomains || [])].map(hostFromValue).filter(Boolean)
  );

  const storefrontUrl = (value) => {
    const rawValue = String(value || '').trim();
    if (!rawValue || rawValue.startsWith('#')) return null;

    const resourceMatch = rawValue.match(/^shopify:\/\/(collections|pages|products)\/([^?#]+)(\?[^#]*)?(#.*)?$/i);
    if (resourceMatch) {
      const resourcePath = `/${resourceMatch[1].toLowerCase()}/${resourceMatch[2].replace(/^\/+|\/+$/g, '')}`;
      return new URL(`${resourcePath}${resourceMatch[3] || ''}${resourceMatch[4] || ''}`, window.location.origin);
    }

    let parsed;
    try {
      parsed = new URL(rawValue, window.location.origin);
    } catch (error) {
      return null;
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;

    const host = parsed.hostname.toLowerCase();
    const belongsToStore =
      parsed.origin === window.location.origin || shopHosts.has(host) || host.endsWith('.myshopify.com');
    if (!belongsToStore) return null;

    return new URL(`${parsed.pathname}${parsed.search}${parsed.hash}`, window.location.origin);
  };

  const rootUrl = storefrontUrl(config.rootUrl);
  const campaignHomeUrl = storefrontUrl(config.campaignHomeUrl);
  const campaignCollectionUrl = storefrontUrl(config.campaignCollectionUrl);

  const cleanPath = (url) => (url ? url.pathname.replace(/\/+$/, '') || '/' : '');
  const rootPath = cleanPath(rootUrl);

  const matchesTarget = (url, target) => {
    if (!url || !target || cleanPath(url) !== cleanPath(target)) return false;
    for (const [key, value] of target.searchParams) {
      if (key === markerKey) continue;
      if (url.searchParams.get(key) !== value) return false;
    }
    return true;
  };

  const isCollectionUrl = (url, rawHref = '') => {
    if (/^shopify:\/\/collections\//i.test(rawHref)) return true;
    if (!url) return false;
    const parts = cleanPath(url).split('/').filter(Boolean);
    return parts.includes('collections') && parts.indexOf('collections') < parts.length - 1;
  };

  const routedTarget = (target, keepCampaignActive = false) => {
    if (!target) return '';
    const destination = new URL(target.href);

    if (keepCampaignActive) destination.searchParams.set(markerKey, '1');
    else destination.searchParams.delete(markerKey);

    const currentParams = new URL(window.location.href).searchParams;
    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      if (currentParams.has(key) && !destination.searchParams.has(key)) {
        destination.searchParams.set(key, currentParams.get(key));
      }
    });

    return destination.href;
  };

  const visibilityContainer = (element) => {
    if (element.matches('li')) return element;
    const listItem = element.closest('li:not(.price__sale):not(.price__regular)');
    if (listItem) return listItem;

    const productInfo = element.closest('product-info');
    if (productInfo) return productInfo.closest('.shopify-section') || productInfo;

    return element.closest('article') || element;
  };

  const syncVisibility = (scope = document) => {
    const selector = '[data-campaign-product], [data-campaign-collection]';
    const elements = [];
    if (scope.nodeType === Node.ELEMENT_NODE && scope.matches(selector)) elements.push(scope);
    elements.push(...scope.querySelectorAll(selector));

    elements.forEach((element) => {
      const campaignOnly = element.dataset.campaignProduct === 'true' || element.dataset.campaignCollection === 'true';
      const shouldShow = config.active || !campaignOnly;
      const container = visibilityContainer(element);
      container.classList.toggle('campaign-catalog__hidden-item', !shouldShow);

      if (shouldShow && container.dataset.campaignHiddenByRouter === 'true') {
        container.removeAttribute('hidden');
        container.removeAttribute('aria-hidden');
        delete container.dataset.campaignHiddenByRouter;
      } else if (!shouldShow) {
        container.hidden = true;
        container.setAttribute('aria-hidden', 'true');
        container.dataset.campaignHiddenByRouter = 'true';
      }
    });
  };

  const routedHref = (anchor) => {
    if (!anchor) return '';

    if (config.active && anchor.matches('[data-campaign-destination="home"]') && campaignHomeUrl) {
      return routedTarget(campaignHomeUrl, true);
    }
    if (config.active && anchor.matches('[data-campaign-destination="collection"]') && campaignCollectionUrl) {
      return routedTarget(campaignCollectionUrl, true);
    }

    const rawHref = anchor.getAttribute('href') || '';
    const url = storefrontUrl(rawHref);
    if (!url) return '';

    if (config.active) {
      if (
        campaignHomeUrl &&
        (anchor.matches('.header__heading-link') || cleanPath(url) === rootPath || matchesTarget(url, campaignHomeUrl))
      ) {
        return routedTarget(campaignHomeUrl, true);
      }

      if (
        campaignCollectionUrl &&
        isCollectionUrl(url, rawHref)
      ) {
        const collectionTarget = matchesTarget(url, campaignCollectionUrl) ? url : campaignCollectionUrl;
        return routedTarget(collectionTarget, true);
      }
    } else {
      if (rootUrl && (matchesTarget(url, campaignHomeUrl) || matchesTarget(url, campaignCollectionUrl))) {
        return routedTarget(rootUrl);
      }
    }

    return '';
  };

  const syncLinks = (scope = document) => {
    const anchors = [];
    if (scope.nodeType === Node.ELEMENT_NODE && scope.matches('a[href]')) anchors.push(scope);
    anchors.push(...scope.querySelectorAll('a[href]'));

    anchors.forEach((anchor) => {
      const replacement = routedHref(anchor);
      if (!replacement || anchor.href === replacement) return;
      anchor.setAttribute('href', replacement);
      anchor.dataset.campaignRouted = 'true';
    });
  };

  const sync = (scope = document) => {
    syncVisibility(scope);
    syncLinks(scope);
  };

  sync();

  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const replacement = routedHref(anchor);
      if (!replacement) return;
      anchor.setAttribute('href', replacement);
      anchor.dataset.campaignRouted = 'true';
    },
    true
  );

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        syncLinks(mutation.target);
        return;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) sync(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['href'],
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', (event) => sync(event.target));
})();
